import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { User, Role } from '@/types/User'
import { authService } from '@/services/AuthService'
import { tokenService } from '@/services/TokenService'
import type { LoginRequest, RegisterRequest, VerifyOtpRequest, AuthResponse } from '@/types/ApiResponse'

export const useAuthStore = defineStore('auth', () => {
    // Initialize state from Storage
    const user = ref<User | null>(tokenService.getUser())
    const isAuthenticated = ref<boolean>(tokenService.isAuthenticated())
    const lastFetchedAt = ref<number>(0)
    const STALE_THRESHOLD = 30_000 // 30 seconds

    const login = async (credentials: LoginRequest): Promise<AuthResponse> => {
        const response = await authService.login(credentials)
        if (!response.mfaRequired) {
            isAuthenticated.value = true
            await restoreSession()
        }
        return response
    }

    const register = async (userData: RegisterRequest): Promise<AuthResponse> => {
        const response = await authService.register(userData)
        if (!response.mfaRequired) {
            isAuthenticated.value = true
            user.value = tokenService.getUser()
        }
        return response
    }

    const verifyOtp = async (request: VerifyOtpRequest): Promise<AuthResponse> => {
        const response = await authService.verifyOtp(request)
        isAuthenticated.value = true
        // Fetch full user profile from backend
        await restoreSession()
        return response
    }

    const logout = async () => {
        const [{ useChatStore }, { usePresenceStore }, { teardownWebPush }] = await Promise.all([
            import('./chat'),
            import('./presence'),
            import('@/utils/webPush')
        ])
        useChatStore().disconnect()
        usePresenceStore().reset()
        // Oturum düşmeden önce: web push aboneliği tarayıcıdan + backend'ten kaldırılır.
        await teardownWebPush()

        await authService.logout()

        user.value = null
        isAuthenticated.value = false
    }

    /**
     * Restore session from backend /me endpoint.
     * Always attempts /me call to validate httpOnly cookies — even if localStorage flag is missing.
     * Skips fetch only if data is fresh (within STALE_THRESHOLD) unless force=true.
     */
    const restoreSession = async (force = false) => {
        try {
            // Skip only if we already have fresh user data
            if (!force && user.value && isAuthenticated.value &&
                (Date.now() - lastFetchedAt.value) < STALE_THRESHOLD) {
                return
            }

            // Always call /me — httpOnly cookies may be valid even if localStorage is empty
            const userData = await authService.getMe()
            user.value = userData
            isAuthenticated.value = true
            tokenService.setAuthenticated(true, tokenService.isPersistent())
            lastFetchedAt.value = Date.now()
        } catch (error: any) {
            // Only clear auth state on 401 (unauthorized) — not on network/500 errors
            const status = error?.response?.status ?? error?.status
            if (status === 401) {
                user.value = null
                isAuthenticated.value = false
                tokenService.clearAuthState()
            }
        }
    }

    const hasPermission = (requiredPermission: string): boolean => {
        return user.value?.permissions?.includes(requiredPermission) ?? false
    }

    const hasRole = (requiredRole: Role): boolean => {
        return user.value?.role === requiredRole
    }

    return {
        user,
        isAuthenticated,
        login,
        register,
        verifyOtp,
        logout,
        restoreSession,
        hasPermission,
        hasRole
    }
})
